import { green, indigo, yellow, lightBlue } from "@mui/material/colors";

// Preset column configurations
export const columnPresets = {
  default: {
    label: "Default View",
    description: "Essential columns for daily workflow",
  },
  analytics: {
    label: "Analytics View",
    description: "Full data view for detailed analysis",
  },
  quick: {
    label: "Quick Review",
    description: "Minimal view for fast decision making",
  },
  custom: {
    label: "Custom",
    description: "Custom columns",
  },
};

export const tableGroups = {
  // General Info fields
  general: {
    label: "General Info",
    color: indigo["A100"],
    default_db_table: "influencers",
    fields: [
      { db_column: "status", visibleInPresets: ["default", "analytics", "quick"], type: "select" },
      { db_column: "username", visibleInPresets: ["default", "analytics", "quick"], type: "text" },
      { db_column: "refusal_reason", visibleInPresets: [], type: "text" },
      { db_column: "notes", db_table: "internal_reviews", visibleInPresets: ["default", "analytics"], type: "text" },
      { db_column: "platform", visibleInPresets: ["default", "analytics", "quick"], type: "select" },
      { db_column: "created_at", visibleInPresets: ["default", "analytics"], type: "date" },
      {
        db_column: "assigned_member",
        visibleInPresets: ["default", "analytics"],
        label: "Responsible",
        type: "select",
      },
      { db_column: "id", visibleInPresets: ["default", "analytics", "quick"], type: "text" },
    ],
  },
  // Free Check Data
  free_check: {
    label: "Free Check Data",
    color: green[200],
    default_db_table: "influencer_free_checks",
    fields: [
      { db_column: "public_email", visibleInPresets: ["default", "analytics"], type: "text" },
      { db_column: "followers", visibleInPresets: ["default", "analytics", "quick"], type: "number" },
      { db_column: "quality_score", visibleInPresets: ["default", "analytics", "quick"], type: "number" },
      { db_column: "language", visibleInPresets: ["analytics"], type: "text" },
      { db_column: "country", visibleInPresets: ["default"], type: "text" },
      { db_column: "city", visibleInPresets: [], type: "text" },
      { db_column: "bio", visibleInPresets: ["default"], type: "text" },
      { db_column: "website", visibleInPresets: [], type: "text" },
      { db_column: "profile_pic_url", visibleInPresets: [], type: "text" },
      { db_column: "insta_category", visibleInPresets: [], type: "text" },
      { db_column: "reach", visibleInPresets: ["analytics"], type: "number" },
      { db_column: "posts", visibleInPresets: ["analytics"], type: "number" },
      { db_column: "gender", visibleInPresets: [], type: "select" },
      { db_column: "is_private", visibleInPresets: [], type: "select" },
      { db_column: "median_views_per_post", visibleInPresets: ["analytics"], type: "number" },
      { db_column: "median_plays_per_reel", visibleInPresets: ["analytics"], type: "number" },
      { db_column: "name", visibleInPresets: [], type: "text" },
      { db_column: "following", visibleInPresets: [], type: "number" },
      { db_column: "display_name", visibleInPresets: ["analytics"], type: "text" },
      { db_column: "firstname", visibleInPresets: ["default", "analytics"], type: "text" },
      { db_column: "avg_likes_per_post", visibleInPresets: ["analytics"], label: "Avg Likes/Post", type: "number" },
      {
        db_column: "avg_comments_per_post",
        visibleInPresets: ["analytics"],
        label: "Avg Comments/Post",
        type: "number",
      },
      { db_column: "contact_email", visibleInPresets: ["analytics"], type: "text" },
      { db_column: "contact_whatsapp", visibleInPresets: ["analytics"], type: "text" },
      { db_column: "contact_link", visibleInPresets: ["analytics"], type: "text" },
      { db_column: "management_company", visibleInPresets: ["analytics"], type: "text" },
      { db_column: "youtube_channel", visibleInPresets: ["analytics"], type: "text" },
      { db_column: "youtube_username", visibleInPresets: ["analytics"], type: "text" },
      { db_column: "monthly_growth_difference", visibleInPresets: ["analytics"], type: "number" },
      { db_column: "monthly_growth_change", visibleInPresets: ["analytics"], type: "number" },
      { db_column: "brand_mentions", visibleInPresets: ["analytics"], type: "text" },
      { db_column: "stars_rating", visibleInPresets: ["analytics"], type: "number" },
      { db_column: "instagram_id", visibleInPresets: ["analytics"], type: "text" },
    ],
  },
  // Paid Check Data
  paid_check: {
    label: "Paid Check Data",
    color: yellow[300],
    default_db_table: "influencer_paid_checks",
    fields: [
      {
        db_column: "authenticity_score",
        visibleInPresets: ["default", "analytics", "quick"],
        type: "number",
      },
      { db_column: "email", visibleInPresets: [], type: "text" },
      {
        db_column: "audience_gender_female",
        visibleInPresets: ["default", "analytics"],
        type: "percent",
      },
      { db_column: "audience_gender_male", visibleInPresets: ["analytics"], type: "percent" },
      {
        db_column: "audience_dach",
        visibleInPresets: ["default", "analytics"],
        label: "DACH Audience %",
        type: "percent",
      },
      {
        db_column: "language_german",
        visibleInPresets: ["default", "analytics"],
        label: "German Language %",
        type: "percent",
      },
      { db_column: "audience_language", visibleInPresets: ["analytics", "quick"], type: "text" },
      { db_column: "audience_median_age", visibleInPresets: ["default", "analytics", "quick"], type: "number" },
      {
        db_column: "audience_real_percentage",
        visibleInPresets: ["default", "analytics"],
        type: "percent",
      },
      { db_column: "audience_city", visibleInPresets: ["analytics"], type: "text" },
      {
        db_column: "audience_influencers_percentage",
        visibleInPresets: ["analytics"],
        type: "percent",
      },
      {
        db_column: "audience_mass_followers_percentage",
        visibleInPresets: ["analytics"],
        type: "percent",
      },
      {
        db_column: "audience_suspicious_percentage",
        visibleInPresets: ["analytics"],
        type: "percent",
      },
      { db_column: "audience_countries_full", visibleInPresets: ["analytics"], label: "All Countries %", type: "text" },
      { db_column: "audience_languages_full", visibleInPresets: ["analytics"], label: "All Languages %", type: "text" },
      { db_column: "audience_cities_full", visibleInPresets: ["analytics"], label: "Top Cities %", type: "text" },
      {
        db_column: "audience_interests_full",
        visibleInPresets: ["analytics"],
        label: "Audience Interests %",
        type: "text",
      },
      {
        db_column: "audience_age_13_17",
        visibleInPresets: ["analytics"],
        label: "Age 13-17 %",
        type: "percent",
      },
      {
        db_column: "audience_age_18_24",
        visibleInPresets: ["analytics"],
        label: "Age 18-24 %",
        type: "percent",
      },
      {
        db_column: "audience_age_25_34",
        visibleInPresets: ["analytics"],
        label: "Age 25-34 %",
        type: "percent",
      },
      {
        db_column: "audience_age_35_44",
        visibleInPresets: ["analytics"],
        label: "Age 35-44 %",
        type: "percent",
      },
      {
        db_column: "audience_age_45_plus",
        visibleInPresets: ["analytics"],
        label: "Age 45+ %",
        type: "percent",
      },
      { db_column: "average_age", visibleInPresets: ["analytics"], type: "number" },
      { db_column: "sponsored_posts_percentage", visibleInPresets: [], type: "percent" },
      { db_column: "posts_per_week", visibleInPresets: [], type: "number" },
      { db_column: "sponsored_reels_percentage", visibleInPresets: [], type: "percent" },
      { db_column: "reels_per_week", visibleInPresets: [], type: "number" },
      { db_column: "monthly_growth_followers", visibleInPresets: ["analytics"], type: "number" },
      {
        db_column: "monthly_growth_percentage",
        visibleInPresets: ["analytics"],
        type: "percent",
      },
      { db_column: "story_views_min", visibleInPresets: ["analytics"], type: "number" },
      { db_column: "story_views_max", visibleInPresets: ["analytics"], type: "number" },
      { db_column: "story_views_avg", visibleInPresets: ["analytics"], type: "number" },
      { db_column: "story_frequency", visibleInPresets: ["analytics"], type: "text" },
    ],
  },
  // Manually added data
  manual: {
    label: "Manual Check Data",
    color: lightBlue[200],
    default_db_table: "internal_reviews",
    fields: [
      { db_column: "content_type_validation", visibleInPresets: ["default", "analytics", "quick"], type: "select" },
      { db_column: "nail_situation", visibleInPresets: ["default", "analytics"], type: "select" },
      { db_column: "talks_to_cam", visibleInPresets: ["default", "analytics"], type: "select" },
    ],
  },
};

export const getAllFields = () => {
  const allFields = [];
  for (const groupKey of Object.keys(tableGroups)) {
    const group = tableGroups[groupKey];
    for (const field of group.fields) {
      allFields.push({
        ...field,
        key: field.db_column,
        label: field.label || field.db_column.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        db_table: field.db_table || group.default_db_table,
        group: groupKey,
        color: group.color,
        type: field.type || "text",
      });
    }
  }
  return allFields;
};

export const getTableForColumn = ({ columnKey }) => {
  const fields = getAllFields();
  return fields.find((field) => field.db_column === columnKey)?.db_table;
};

export const getCellColor = ({ columnKey }) => {
  const fields = getAllFields();
  return fields.find((field) => field.db_column === columnKey)?.color || "action.selected";
};

export const getPresetColumns = ({ presetKey }) => {
  const fields = getAllFields();
  return fields.filter((field) => field.visibleInPresets.includes(presetKey)).map((field) => field.db_column);
};

export const getTableHeaders = ({ columns }) => {
  const fields = getAllFields();
  if(columns === "all") return fields;
  return fields.filter(field => columns.includes(field.db_column));
}

export const getFieldByKey = ({ columnKey }) => {
  const fields = getAllFields();
  return fields.find((field) => field.db_column === columnKey);
};

export const columnTypes = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "percent", label: "Percent" },
  { value: "date", label: "Date" },
  { value: "select", label: "Option" },
];

export const getConditionsForType = (type) => {
  switch (type) {
    case "number":
    case "percent":
      return [
        { value: "less_than", label: "Less than" },
        { value: "less_than_equal", label: "Less than or equal to" },
        { value: "greater_than", label: "Greater than" },
        { value: "greater_than_equal", label: "Greater than or equal to" },
        { value: "equals", label: "Equals" },
        { value: "not_equal", label: "Not equal to" },
        { value: "between", label: "Between" },
      ];
    case "text":
      return [
        { value: "equals", label: "Equals" },
        { value: "not_equal", label: "Does not equal" },
        { value: "contains", label: "Contains" },
        { value: "not_contains", label: "Does not contain" },
        { value: "starts_with", label: "Starts with" },
        { value: "ends_with", label: "Ends with" },
        { value: "is_empty", label: "Is empty" },
        { value: "is_not_empty", label: "Is not empty" },
      ];
    case "date":
      return [
        { value: "before", label: "Before" },
        { value: "after", label: "After" },
        { value: "on_or_before", label: "On or before" },
        { value: "on_or_after", label: "On or after" },
        { value: "today", label: "Today" },
        { value: "yesterday", label: "Yesterday" },
        { value: "tomorrow", label: "Tomorrow" },
        { value: "in_last_weeks", label: "In the last weeks" },
        { value: "in_last_months", label: "In the last months" },
        { value: "in_last_years", label: "In the last years" },
        { value: "in_next_weeks", label: "In the next weeks" },
        { value: "in_next_months", label: "In the next months" },
        { value: "in_next_years", label: "In the next years" },
      ];
    case "select":
      return [
        { value: "contains_any", label: "Contains any of" },
        { value: "contains_all", label: "Contains all of" },
        { value: "not_contains", label: "Does not contain" },
        { value: "is_empty", label: "Is empty" },
        { value: "is_not_empty", label: "Is not empty" },
      ];
    default:
      return [];
  }
};

